import React, { useContext, useEffect, useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useDropzone } from "react-dropzone";

import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { parse } from "csv-parse/browser/esm";

export interface FileToReturn extends File {
  csvHeaders?: boolean;
}

export interface CsvPreview {
  fileName: string;
  columns: GridColDef[];
  rows: Array<{ id: string; [key: string]: string }>;
}

interface CsvPreviewEntry {
  columns: GridColDef[];
  rows: Array<{ id: string; [key: string]: string }>;
  headers: boolean;
  processed: boolean; // checks if the file had already been loaded into the preview
}

// Combining DataGrid's `autoHeight` with an outer `overflow: auto` wrapper
// creates two competing scroll owners: autoHeight disables the grid's own
// virtualized scroll container (it renders every row's full height), so the
// grid's internal cell-focus/keyboard-nav scrolling and the outer Box's
// scrollbar fight each other, producing inconsistent/janky scroll behavior.
// Giving the grid a real fixed height instead (capped at PREVIEW_MAX_HEIGHT,
// but no taller than the content needs) lets DataGrid own its scrolling
// entirely -- a single, correctly-functioning scroll container.
const PREVIEW_ROW_HEIGHT = 52;
const PREVIEW_HEADER_HEIGHT = 56;
const PREVIEW_FOOTER_HEIGHT = 52;
const PREVIEW_MAX_HEIGHT = 400;
export const CSV_PREVIEW_PAGE_SIZE = 50;

export const getCsvPreviewGridHeight = (rowCount: number): number => {
  // The footer (pagination controls) is hidden entirely when everything
  // fits on one page -- don't reserve height for it in that case either,
  // or it'd leave the same kind of dead space this height calc exists to
  // avoid.
  const footerHeight =
    rowCount > CSV_PREVIEW_PAGE_SIZE ? PREVIEW_FOOTER_HEIGHT : 0;
  return Math.min(
    PREVIEW_MAX_HEIGHT,
    PREVIEW_HEADER_HEIGHT + rowCount * PREVIEW_ROW_HEIGHT + footerHeight,
  );
};

interface DragAndDropProps {
  acceptedFileTypes: { [key: string]: string[] };
  setDragAndDropFiles: (files: FileToReturn[]) => void;
  maxAcceptedFiles?: number;
  files: FileToReturn[];
  setFiles: React.Dispatch<React.SetStateAction<FileToReturn[]>>;
  removeFilePreview?: (index: number) => void;
  // When true, suppresses the inline CSV data grid (the "First row is
  // headers" toggle still renders inline) -- pair with onCsvPreviewChange
  // to render the grid elsewhere (e.g. a full-width row instead of a
  // narrow column).
  hideInlineGrid?: boolean;
  onCsvPreviewChange?: (previews: CsvPreview[]) => void;
}

const DragAndDrop: React.FC<DragAndDropProps> = ({
  acceptedFileTypes,
  setDragAndDropFiles,
  maxAcceptedFiles,
  files,
  setFiles,
  hideInlineGrid,
  onCsvPreviewChange,
}) => {
  const { showError } = useContext(SnackbarContext);
  const [csvPreviewData, setCsvPreviewData] = useState<
    Map<string, CsvPreviewEntry>
  >(new Map());

  // Prune preview data for any file no longer present (removed, or replaced
  // by a new drop) -- otherwise a stale entry lingers and would leak into
  // onCsvPreviewChange for a file that's no longer attached.
  useEffect(() => {
    setCsvPreviewData((prev) => {
      const currentNames = new Set(files.map((file) => file.name));
      let changed = false;
      const next = new Map<string, CsvPreviewEntry>();
      prev.forEach((value, key) => {
        if (currentNames.has(key)) {
          next.set(key, value);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [files]);

  useEffect(() => {
    if (!onCsvPreviewChange) {
      return;
    }
    onCsvPreviewChange(
      Array.from(csvPreviewData.entries()).map(([fileName, data]) => ({
        fileName,
        columns: data.columns,
        rows: data.rows,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvPreviewData]);

  const updateFiles = (newState: File[]) => {
    if (maxAcceptedFiles && newState.length > maxAcceptedFiles) {
      showError(
        new Error(
          `Maximum of ${maxAcceptedFiles} file(s) allowed.`,
        ) as GrpcError,
      );
      return;
    }
    setFiles([...newState]);
    const updatedFiles = newState.map((file) => {
      if (file.type.startsWith("text/csv")) {
        const csvHeaders = csvPreviewData.has(file.name)
          ? csvPreviewData.get(file.name)?.headers
          : false;
        return Object.assign(file, { csvHeaders }) as FileToReturn;
      }
      return file as FileToReturn;
    });
    setDragAndDropFiles([...updatedFiles]);
  };

  const onDrop = (acceptedFiles: File[]) => {
    // Dropping a new file when already at the limit replaces the oldest
    // file(s) rather than rejecting the drop -- a single-file dropzone
    // (every consumer today uses maxAcceptedFiles={1}) should let a new
    // drop swap out the old file without requiring it to be removed first.
    const combined = [...files, ...acceptedFiles];
    const nextFiles = maxAcceptedFiles
      ? combined.slice(-maxAcceptedFiles)
      : combined;
    updateFiles(nextFiles);

    acceptedFiles
      .filter((file) => nextFiles.includes(file))
      .forEach(async (file) => {
        if (file.type.startsWith("text/csv")) {
          await updateCSVPreview(file);
        }
      });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
  });

  const displayIMGPreview = (file: File) => {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
          style={{ width: "500px" }}
        />
      </Box>
    );
  };
  const displayCSVPreview = (file: File) => {
    return (
      <Box>
        {!hideInlineGrid && (
          <Box
            sx={{
              height: getCsvPreviewGridHeight(
                csvPreviewData.get(file.name)?.rows.length || 0,
              ),
              width: "100%",
            }}
          >
            <DataGrid
              columns={csvPreviewData.get(file.name)?.columns || []}
              rows={csvPreviewData.get(file.name)?.rows || []}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: CSV_PREVIEW_PAGE_SIZE },
                },
              }}
              hideFooter={
                (csvPreviewData.get(file.name)?.rows.length || 0) <=
                CSV_PREVIEW_PAGE_SIZE
              }
            />
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={csvPreviewData.get(file.name)?.headers}
                value={csvPreviewData.get(file.name)?.headers}
                onChange={async (e) => {
                  setCsvPreviewData((currentData) => {
                    const existingData = currentData.get(file.name);
                    if (existingData) {
                      currentData.set(file.name, {
                        ...existingData,
                        headers: e.target.checked,
                        processed: true, // Mark as processed so we don't force the file headers as true again
                      });
                    }
                    return currentData;
                  });
                  await updateCSVPreview(file);
                  updateFiles(files);
                }}
              />
            }
            label="First row is headers"
          />
        </Box>
      </Box>
    );
  };
  const updateCSVPreview = async (file: File) => {
    const readFileAsText = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          reject(new Error("Error reading file"));
        };
        reader.readAsText(file);
      });
    };

    try {
      const text = await readFileAsText(file);

      parse(text, { skip_empty_lines: true }, (err, output: string[][]) => {
        if (err || output.length === 0) {
          return;
        }

        let columnDefs: GridColDef[];
        let headers: boolean;

        // checks on first load the if the first column of the first row is "user_id" and if so load in as headers: true
        if (
          output.length > 0 &&
          output[0].length > 0 &&
          output[0][0].trim().includes("_id") &&
          (!csvPreviewData.has(file.name) ||
            !csvPreviewData.get(file.name)?.processed)
        ) {
          columnDefs = output[0].map((header) => ({
            field: header,
            headerName: header,
            flex: 1,
            minWidth: 130,
          }));
          headers = true;
          output.shift(); // Remove headers from rows
        } else if (
          !csvPreviewData.has(file.name) ||
          !csvPreviewData.get(file.name)?.headers
        ) {
          columnDefs = Array.from({ length: output[0].length }, (_, index) => ({
            field: index.toString(),
            headerName: index.toString(),
            flex: 1,
            minWidth: 130,
          }));
          headers = false;
        } else {
          columnDefs = output[0].map((header) => ({
            field: header,
            headerName: header,
            flex: 1,
            minWidth: 130,
          }));
          headers = true;
          output.shift(); // Remove headers from rows
        }

        const rows = output.map((row: string[], rowIndex: number) => {
          const rowData: { [key: string]: string } = {};
          row.forEach((cell, colIndex) => {
            const headerName = columnDefs[colIndex]?.headerName;
            if (headerName) {
              rowData[headerName] = cell;
            }
          });
          return {
            id: rowIndex.toString(),
            ...rowData,
          };
        });

        setCsvPreviewData((prevData) => {
          const newData = new Map(prevData);
          newData.set(file.name, {
            columns: columnDefs,
            rows,
            headers,
            processed: true,
          });
          return newData;
        });

        setFiles((prevFiles) => {
          return prevFiles.map((prevFile) => {
            if (prevFile.name === file.name) {
              return Object.assign(prevFile, {
                csvHeaders: headers,
              }) as FileToReturn;
            }
            return prevFile as FileToReturn;
          });
        });
      });
    } catch (error) {
      console.error("Error processing file:", error);
    }
  };

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed #ccc",
          borderRadius: "8px",
          padding: "20px",
          textAlign: "center",
          backgroundColor: isDragActive ? "#f0f8ff" : "#fafafa",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? isDragActive
                ? "#505050"
                : "#101010"
              : isDragActive
                ? "#f0f8ff"
                : "#fafafa",
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} data-testid="drag-and-drop" />
        <Typography variant="body1">
          {isDragActive
            ? "Drop the files here ..."
            : "Drag & drop some files here, or click to select files"}
        </Typography>
      </Box>

      <Box sx={{ mt: 2 }}>
        {files.map((file, index) => (
          <Box key={index} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography>{file.name}</Typography>
              <IconButton
                size="small"
                color="error"
                aria-label="Remove file"
                onClick={() => {
                  updateFiles(files.filter((_, i) => i !== index));
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            {file.type.startsWith("image/") && displayIMGPreview(file)}
            {file.type.startsWith("text/csv") &&
              csvPreviewData.has(file.name) &&
              displayCSVPreview(file)}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default DragAndDrop;
